import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, CheckCircle } from "lucide-react";
import { apiGet, apiGetBlob, apiPost, apiDelete } from "../Api/Client.js";
import { discardRecorder, replaceSample, sampleSource } from "./voiceSamples.js";
import { Alert, Badge, Button, Card, EmptyState, useConfirm, useToast } from "../Ui/index.jsx";
import "../Styles/ProfileScreen.css";

const LANGUAGES = [
  {
    key: "en",
    label: "English",
    prompt: (name) =>
      `Hello, My Name Is ${name}. Today I Am Recording This Voice Sample For Speaker Identification. The Quick Brown Fox Jumps Over The Lazy Dog. I Speak Naturally And Clearly.`,
  },
  {
    key: "regional",
    label: "Regional Language",
    prompt: (name) =>
      `Read A Natural Sentence In Your Regional Language. Example : \nनमस्ते, मेरा नाम ${name} है। मैं यह आवाज़ का नमूना रिकॉर्ड कर रहा हूँ ताकि सिस्टम भविष्य की बैठकों में मेरी पहचान कर सके। मैं सामान्य गति से और स्पष्ट रूप से बोल रहा हूँ।`,
  },
];

export default function VoiceRecorder({ employeeId, employeeName, onChange }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const audioRef = useRef(null);
  const name = employeeName || "User";

  const fetchRecordings = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/Users/VoiceRecordings/?employee=${employeeId}`);
      setRecordings(Array.isArray(data) ? data : data?.results || []);
      setLoadFailed(false);
    } catch {
      // Not an empty list: showing "no samples" would invite recording duplicates.
      setLoadFailed(true);
    } finally {
      setLoading(false);
      if (onChange) onChange();
    }
  };

  useEffect(() => {
    if (employeeId) fetchRecordings(); else setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    return () => {
      discardRecorder(mediaRecorder.current);
      stopAudio();
    };
  }, []);

  // Stops the sample that is playing and frees its local copy.
  const stopAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    if (audio.src.startsWith("blob:")) URL.revokeObjectURL(audio.src);
    audioRef.current = null;
  };

  const hasEnglish = recordings.some((r) => r.language === "en");
  const hasRegional = recordings.some((r) => r.language === "regional");
  const bothDone = hasEnglish && hasRegional;

  const startRecording = async (lang) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorder.current = recorder;
      // The samples of this language that exist now are replaced only once the new one is saved.
      const previousIds = recordings.filter((r) => r.language === lang).map((r) => r.id);
      chunks.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: recorder.mimeType });
        await uploadRecording(blob, lang, previousIds);
      };
      recorder.start();
      setActiveLang(lang);
    } catch {
      toast("The microphone is blocked or not available. Allow microphone access in your browser, then try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
      setActiveLang(null);
    }
  };

  const uploadRecording = async (blob, lang, previousIds = []) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("employee", employeeId);
      fd.append("language", lang);
      fd.append("title", `${lang === "en" ? "English" : "Regional"} Voice Sample - ${new Date().toLocaleDateString()}`);
      fd.append("audio_file", blob, `recording_${lang}_${Date.now()}.wav`);
      const { stuck } = await replaceSample({
        upload: () => apiPost("/Users/VoiceRecordings/", fd),
        remove: (id) => apiDelete(`/Users/VoiceRecordings/${id}/`),
        previousIds,
      });
      await fetchRecordings();
      if (stuck.length) toast("Your new sample is saved, but the old one could not be removed. Please delete it yourself.", "info");
    } catch (e) {
      toast(`The recording was not saved. ${e.message || "Please try again."}`);
    } finally {
      setUploading(false);
    }
  };

  const togglePlay = async (rec) => {
    stopAudio();
    if (playing === rec.id) {
      setPlaying(null);
      return;
    }
    setPlaying(rec.id);
    try {
      const src = await sampleSource(apiGetBlob, rec.audio_url);
      if (!src) { setPlaying(null); return; }
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.onended = () => { stopAudio(); setPlaying(null); };
      audio.play().catch(() => {});
    } catch {
      setPlaying(null);
      toast("This sample could not be played.");
    }
  };

  const deleteRecording = (id) => confirm.ask({
    title: "Delete this recording?",
    message: "You can record a new one any time.",
    confirmLabel: "Delete recording",
    tone: "danger",
    onConfirm: () => removeRecording(id),
  });

  const removeRecording = async (id) => {
    try {
      await apiDelete(`/Users/VoiceRecordings/${id}/`);
      setRecordings((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast("The recording was not deleted. Please try again.");
    }
  };

  const getRecording = (lang) => recordings.find((r) => r.language === lang);

  return (
    <div className="VR">
      <div className="VR-Head">
        <Mic size={16} aria-hidden="true" />
        <strong>Voice Samples</strong>
        {bothDone && <Badge variant="success"><CheckCircle size={12} aria-hidden="true" /> Complete</Badge>}
      </div>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : loadFailed ? (
        <Alert className="VR-Failed" tone="danger">
          Your voice samples could not be loaded.
          <Button size="sm" onClick={fetchRecordings}>Try again</Button>
        </Alert>
      ) : (
        LANGUAGES.map((lang) => {
          const rec = getRecording(lang.key);
          const isRecording = activeLang === lang.key;
          return (
            <Card key={lang.key} className="VR-Lang">
              <div className="VR-Lang__head">
                <strong>{lang.label}</strong>
                {rec && <Badge variant="success"><CheckCircle size={12} aria-hidden="true" /> Recorded</Badge>}
                {isRecording && <Badge variant="danger" dot>Recording {lang.label.toLowerCase()}...</Badge>}
              </div>

              <p className="ds-small VR-Prompt">{lang.prompt(name)}</p>

              {rec && (
                <div className="VR-Play">
                  <Button variant="ghost" size="sm" aria-label={playing === rec.id ? "Pause sample" : "Play sample"} onClick={() => togglePlay(rec)}>
                    {playing === rec.id ? <Pause size={14} /> : <Play size={14} />}
                  </Button>
                  <span className="VR-Play__title ds-small">{rec.title}{rec.employee_name ? ` — ${rec.employee_name}` : ""}</span>
                  <Button variant="ghost" size="sm" aria-label="Delete sample" onClick={() => deleteRecording(rec.id)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}

              <div>
                {isRecording ? (
                  <Button variant="danger" size="sm" leftIcon={<Square size={14} />} onClick={stopRecording}>Stop</Button>
                ) : (
                  <Button variant={rec ? undefined : "primary"} size="sm" leftIcon={<Mic size={14} />} onClick={() => startRecording(lang.key)} disabled={uploading || activeLang !== null}>
                    {rec ? "Re-record" : "Record"}
                  </Button>
                )}
              </div>
            </Card>
          );
        })
      )}
      {confirm.dialog}
    </div>
  );
}
